import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useConfirmPasswordReset } from '@/api/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { PasswordStrengthMeter } from '@/components/ui/PasswordStrengthMeter';
import { Label } from '@/components/ui/Label';
import { apiError } from '@/lib/api';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';

export function PasswordResetConfirmPage() {
  usePageTitle('New password');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const mut = useConfirmPasswordReset();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    try {
      await mut.mutateAsync({ token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bb-bg p-4">
      <div className="w-full max-w-md bg-white  shadow-sm border border-bb-lilac/40 p-8">
        <BillerBayLogo variant="dark" height={28} className="mb-5" />
        <h1 className="text-xl font-bold text-bb-navy mb-1">Choose a new password</h1>
        {done ? (
          <div className="text-sm text-green-800 bg-green-50 border border-green-100 px-3 py-3">
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {!token && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2">
                Invalid or expired link.
              </div>
            )}
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordStrengthMeter password={password} />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                invalid={!!confirm && password !== confirm}
                required
              />
              {!!confirm && password !== confirm && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>
              )}
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              loading={mut.isPending}
              disabled={mut.isPending || !token || password.length < 8 || password !== confirm}
              className="w-full justify-center"
            >
              {mut.isPending ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        )}
        <div className="mt-4 text-sm">
          <Link to="/login" className="text-bb-navy hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
